import {SequenceSubtype} from 'constants/ModelTypeConstants';
import {SEQUENCE_TYPE_NATIVE_ENUM_TO_GQL_MAP} from 'constants/SequenceConstants';
import {NucleotideType, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SequenceTypeQuery} from 'molbio/__generated/SequenceTypeQuery';
import {evaluateSequenceType, SEQUENCE_SUBTYPE_DETAILS} from 'molbio/SeqUtil';
import APIUtil from 'util/APIUtil';
import {listToReadable, typeToPluralized} from 'util/NameUtil';
import {ValidateItemsResponse, ValidateWorklistItems} from 'worklists/WorklistConstants';

type SequenceTypeData = ExtractByTypename<SequenceTypeQuery['sequence'], 'SequenceNode'>;

/**
 * Validate a list of item ids, ensuring they are all accessible sequence api ids and pass custom validation.
 *
 * @param itemIds List of item ids to validate.
 * @param errorMessage Message to display if the custom validation fails.
 * @param isValidSequence Custom validation to apply to each sequence.
 */
export async function validateSequenceTypes(
  itemIds: Array<string>,
  errorMessage: string,
  isValidSequence: (sequence: SequenceTypeData) => boolean
): Promise<ValidateItemsResponse> {
  const notNucleotideSequences = itemIds.filter((id) => !APIUtil.isNucleotideSequence(id));
  if (notNucleotideSequences.length) {
    return {
      invalidItemIds: notNucleotideSequences,
      errorMessage,
    };
  }
  // if all items are of type sequence, we need to further verify the types
  const sequenceValidationPromises = itemIds.map((sequenceId) =>
    evaluateSequenceType(sequenceId, (sequenceInfo) => {
      if (isInaccessible(sequenceInfo)) {
        return {
          inaccessible: true,
          invalid: true,
          sequenceId,
        };
      } else {
        return {inaccessible: false, invalid: !isValidSequence(sequenceInfo), sequenceId};
      }
    })
  );
  const sequenceValidationResults = await Promise.all(sequenceValidationPromises);
  const invalidSequences = sequenceValidationResults.filter(
    // Inaccessible sequences should already be filtered out.
    ({inaccessible, invalid}) => !inaccessible && invalid
  );
  if (invalidSequences.length) {
    return {
      invalidItemIds: invalidSequences.map(({sequenceId}) => sequenceId),
      errorMessage,
    };
  } else {
    return {
      invalidItemIds: [],
      errorMessage: null,
    };
  }
}

/**
 * Validate that all the items are DNA sequences or DNA oligos.
 * @param itemIds List of items ids to validate.
 */
export async function validateAllDNASequencesOrOligos(
  itemIds: Array<string>
): Promise<ValidateItemsResponse> {
  return await validateSequenceTypes(
    itemIds,
    'All items in worklist must be DNA sequences or oligos. RNA sequences and oligos are not supported.',
    (sequence) => sequence.nucleotideType === NucleotideType.DNA
  );
}

/**
 * Validate that all the items are DNA sequences. DNA/RNA oligos and RNA sequences are all invalid.
 * @param itemIds List of item ids to validate.
 */
export async function validateAllDNASequences(itemIds: Array<string>): Promise<ValidateItemsResponse> {
  return await validateSequenceTypes(
    itemIds,
    'All items in worklist must be DNA sequences. RNA sequences and DNA/RNA oligos are not supported.',
    (sequence) => sequence.nucleotideType === NucleotideType.DNA && sequence.sequenceType === SequenceType.ds
  );
}

/**
 * Get a validator function that checks that all entities are of the specified sequence subtypes.
 * @param sequenceSubtypes List of sequence subtypes to validate items against.
 */
export function getValidateAllValidSubtypes(sequenceSubtypes: Array<SequenceSubtype>): ValidateWorklistItems {
  const validSubtypesDetails = sequenceSubtypes.map(
    (sequenceSubtype) => SEQUENCE_SUBTYPE_DETAILS[sequenceSubtype]
  );
  const names = sequenceSubtypes.map((sequenceSubtype) => typeToPluralized(sequenceSubtype));
  const errorMessage = `All items in worklist must be ${listToReadable(names, 'or')}.`;
  return (itemIds: Array<string>) =>
    validateSequenceTypes(itemIds, errorMessage, (sequence) =>
      validSubtypesDetails.some(
        ({sequenceType, nucleotideType}) =>
          sequence.nucleotideType === nucleotideType &&
          sequence.sequenceType === SEQUENCE_TYPE_NATIVE_ENUM_TO_GQL_MAP[sequenceType]
      )
    );
}
